import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Users, ShoppingBag, ClipboardList, Radio, CalendarClock, UserPlus, ChevronRight } from 'lucide-react';
import api from '@/lib/api';
import AdminLayout from '@/components/AdminLayout';

const StatCard = ({ label, value, icon: Icon, to, tone = 'default', testid }) => (
  <Link
    to={to}
    className="group block bg-white dark:bg-[#141414] border border-black/5 dark:border-white/10 rounded-2xl p-5 hover:border-[#FF3B30]/40 hover:shadow-sm transition-all"
    data-testid={testid}
  >
    <div className="flex items-center justify-between">
      <span className="text-xs font-bold uppercase tracking-[0.15em] text-gray-500 dark:text-gray-400">
        {label}
      </span>
      <Icon size={18} className={tone === 'accent' ? 'text-[#FF3B30]' : 'text-gray-400 dark:text-gray-500'} />
    </div>
    <div className="mt-3 flex items-end justify-between">
      <p className={`text-3xl font-black tracking-tight ${tone === 'accent' ? 'text-[#FF3B30]' : ''}`} style={{ fontFamily: 'Manrope, sans-serif' }}>
        {Number.isFinite(value) ? value.toLocaleString('ko-KR') : 0}
      </p>
      <ChevronRight size={16} className="text-gray-300 dark:text-gray-600 group-hover:text-[#FF3B30] transition-colors" />
    </div>
  </Link>
);

const AdminDashboard = ({ user }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/admin/stats');
        if (!cancelled) setStats(res.data || {});
      } catch (e) { /* silent */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  const s = stats || {};

  return (
    <AdminLayout user={user} active="dashboard">
      <div className="mb-6">
        <h1 className="text-3xl sm:text-4xl font-black tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="admin-dashboard-title">
          관리자 대시보드
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">LIVEMOA 운영 현황 요약입니다. 카드를 클릭하면 관리 페이지로 이동합니다.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5" data-testid="admin-stat-grid">
        <StatCard label="전체 회원 수"      value={s.total_users}       icon={Users}         to="/admin/users"                             testid="stat-total-users" />
        <StatCard label="전체 판매자 수"    value={s.total_sellers}     icon={ShoppingBag}   to="/admin/sellers"                           testid="stat-total-sellers" />
        <StatCard label="승인 대기 판매자"  value={s.pending_sellers}   icon={ClipboardList} to="/admin/seller-applications?status=pending" tone="accent" testid="stat-pending-sellers" />
        <StatCard label="오늘 방송 예정"    value={s.today_upcoming}    icon={CalendarClock} to="/admin/broadcasts?date=today"             testid="stat-today-upcoming" />
        <StatCard label="현재 LIVE"         value={s.live_now}          icon={Radio}         to="/admin/broadcasts?status=live"            tone="accent" testid="stat-live-now" />
        <StatCard label="오늘 신규 가입자"  value={s.today_new_users}   icon={UserPlus}      to="/admin/users?joined=today"                testid="stat-today-new-users" />
      </div>

      {loading && (
        <p className="mt-6 text-xs text-gray-400" data-testid="admin-stat-loading">불러오는 중…</p>
      )}
    </AdminLayout>
  );
};

export default AdminDashboard;
